import { motion, AnimatePresence } from 'motion/react';
import { useState } from 'react';
import { CheckCircle2, Lightbulb, Loader2, CheckCheck, AlertCircle, Unlock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDailyQuestion } from '../hooks/useDailyQuestion';
import { trackEvent } from '../lib/posthog';

interface Props {
  userId: string;
  onScoreUpdate: () => void;
  onCardUnlocked?: () => void;
}

export function DailyQuestionCard({ userId, onScoreUpdate, onCardUnlocked }: Props) {
  const { t, i18n } = useTranslation();
  const {
    question,
    answers,
    alreadyAnswered,
    selectedAnswerId,
    allCompleted,
    loading,
    submitAnswer,
    result,
  } = useDailyQuestion(userId, i18n.language);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [cardUnlocked, setCardUnlocked] = useState(false);
  const isSubmitted = alreadyAnswered || result !== null;

  const handleSubmit = async () => {
    if (!selectedId || submitting) return;
    setSubmitting(true);
    const res = await submitAnswer(selectedId);
    if (res) {
      trackEvent('daily_answer_submitted', { question_id: question?.id, answer_id: selectedId, score_change: res.score_change });
      onScoreUpdate();
      if (res.card_unlocked) {
        setCardUnlocked(true);
        onCardUnlocked?.();
      }
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-[2.5rem] p-8 shadow-[0_8px_40px_rgba(0,0,0,0.04)] border border-stone-100 flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (allCompleted) {
    return (
      <div className="bg-white rounded-[2.5rem] p-8 shadow-[0_8px_40px_rgba(0,0,0,0.04)] border border-stone-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-emerald-100 p-2 rounded-xl">
            <CheckCheck className="w-5 h-5 text-emerald-600" />
          </div>
          <h3 className="text-xl font-bold text-stone-900 tracking-tight">{t('daily.title')}</h3>
        </div>
        <p className="text-stone-500 font-medium">{t('daily.allCompleted')}</p>
      </div>
    );
  }

  if (!question) return null;

  // Determine the effective selected answer (from DB if already answered, or local state)
  const effectiveSelectedId = alreadyAnswered ? selectedAnswerId : selectedId;

  return (
    <div className="flex flex-col gap-6">
      <motion.div
        layout
        className="bg-white rounded-[2.5rem] p-8 shadow-[0_8px_40px_rgba(0,0,0,0.04)] border border-stone-100"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-amber-100 p-2 rounded-xl">
            <Lightbulb className="w-5 h-5 text-amber-600" />
          </div>
          <h3 className="text-xl font-bold text-stone-900 tracking-tight">{t('daily.title')}</h3>
        </div>

        <p className="text-lg font-medium text-stone-700 mb-8 leading-snug">
          {question.question_text}
        </p>

        <div className="flex flex-col gap-3">
          {answers.map((answer) => {
            const isSelected = effectiveSelectedId === answer.id;
            const showImpact = isSubmitted && answer.score_impact !== undefined;
            const isPositive = showImpact && (answer.score_impact ?? 0) > 0;
            const isNegative = showImpact && (answer.score_impact ?? 0) < 0;

            return (
              <button
                key={answer.id}
                disabled={isSubmitted || submitting}
                onClick={() => setSelectedId(answer.id)}
                className={`
                  w-full p-5 rounded-2xl text-left font-bold transition-all duration-300 border-2
                  ${isSelected && !isSubmitted
                    ? 'bg-stone-900 text-white border-stone-900 scale-[1.02]'
                    : !isSubmitted
                      ? 'bg-stone-50 text-stone-600 border-transparent hover:bg-stone-100'
                      : ''}
                  ${isSubmitted && isSelected && isPositive ? 'border-emerald-500 bg-emerald-50 text-emerald-900' : ''}
                  ${isSubmitted && isSelected && isNegative ? 'border-rose-500 bg-rose-50 text-rose-900' : ''}
                  ${isSubmitted && isSelected && !isPositive && !isNegative ? 'border-stone-400 bg-stone-50 text-stone-700' : ''}
                  ${isSubmitted && !isSelected ? 'bg-stone-50 text-stone-400 border-transparent' : ''}
                  disabled:cursor-default
                `}
              >
                <div className="flex justify-between items-center">
                  <span>{answer.answer_text}</span>
                  {isSubmitted && isSelected && isPositive && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                  {isSubmitted && isSelected && isNegative && <AlertCircle className="w-5 h-5 text-rose-500" />}
                  {showImpact && (
                    <span className={`text-sm font-bold ml-2 ${
                      (answer.score_impact ?? 0) > 0 ? 'text-emerald-500' : (answer.score_impact ?? 0) < 0 ? 'text-rose-500' : 'text-stone-400'
                    }`}>
                      {(answer.score_impact ?? 0) > 0 ? '+' : ''}{answer.score_impact}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <AnimatePresence>
          {!isSubmitted && selectedId && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full mt-8 bg-emerald-500 text-white py-5 rounded-2xl font-bold text-lg shadow-[0_10px_20px_rgba(16,185,129,0.2)] hover:bg-emerald-600 transition-colors disabled:opacity-50"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : t('daily.submit')}
            </motion.button>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {isSubmitted && question.insight_text && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-emerald-900 text-emerald-50 rounded-[2.5rem] p-8 shadow-xl relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-6 opacity-[0.08]">
              <Lightbulb className="w-16 h-16" />
            </div>

            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3">{t('daily.didYouKnow')}</h4>
            <p className="text-emerald-100/80 leading-relaxed font-medium">
              {question.insight_text}
            </p>

            {result && (
              <div className="mt-6 flex items-center gap-2">
                <div className={`px-3 py-1 rounded-full border ${
                  result.score_change > 0
                    ? 'bg-emerald-500/20 border-emerald-500/30'
                    : result.score_change < 0
                      ? 'bg-rose-500/20 border-rose-500/30'
                      : 'bg-stone-500/20 border-stone-500/30'
                }`}>
                  <span className={`text-xs font-bold ${
                    result.score_change > 0 ? 'text-emerald-300' : result.score_change < 0 ? 'text-rose-300' : 'text-stone-300'
                  }`}>
                    {result.score_change > 0 ? '+' : ''}{result.score_change} {t('daily.scorePoints')}
                  </span>
                </div>
                {cardUnlocked && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5, type: 'spring', bounce: 0.4 }}
                    className="px-3 py-1 rounded-full border bg-amber-500/20 border-amber-500/30 flex items-center gap-1.5"
                  >
                    <Unlock className="w-3 h-3 text-amber-300" />
                    <span className="text-xs font-bold text-amber-300">{t('daily.cardUnlocked')}</span>
                  </motion.div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
